import axios from 'axios';

const csrfHeader = () => ({
  'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
});

const parseErrorMessage = (error, fallback) => (
  error?.response?.data?.error || error?.response?.data?.message || fallback
);

const setAiChatVisible = (visible) => ({
  type: 'AI_CHAT_SET_VISIBLE',
  payload: { visible },
});

const setAiChatFeatureEnabled = (enabled) => ({
  type: 'AI_CHAT_SET_FEATURE_ENABLED',
  payload: { enabled },
});

const setAiChatNearBottom = (isNearBottom) => ({
  type: 'AI_CHAT_SET_NEAR_BOTTOM',
  payload: { isNearBottom },
});

const clearAiChatUnseenMessages = () => ({
  type: 'AI_CHAT_CLEAR_UNSEEN_MESSAGES',
});

const selectAiChatConversation = (slug) => ({
  type: 'AI_CHAT_SELECT_CONVERSATION',
  payload: { slug },
});

const getAiChatConversations = () => async (dispatch) => {
  dispatch({ type: 'BEGIN_GET_AI_CHAT_CONVERSATIONS' });

  try {
    const response = await axios({
      method: 'GET',
      url: '/api/v1/ai_chat/conversations',
      headers: csrfHeader(),
    });

    dispatch({
      type: 'FINISH_GET_AI_CHAT_CONVERSATIONS',
      payload: response,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: 'FAILED_GET_AI_CHAT_CONVERSATIONS',
      payload: {
        error: parseErrorMessage(error, 'Failed to load conversations.'),
      },
    });

    return null;
  }
};

const createAiChatConversation = () => async (dispatch) => {
  dispatch({ type: 'BEGIN_CREATE_AI_CHAT_CONVERSATION' });

  try {
    const response = await axios({
      method: 'POST',
      url: '/api/v1/ai_chat/conversations',
      headers: csrfHeader(),
    });

    dispatch({
      type: 'FINISH_CREATE_AI_CHAT_CONVERSATION',
      payload: response,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: 'FAILED_CREATE_AI_CHAT_CONVERSATION',
      payload: {
        error: parseErrorMessage(error, 'Failed to create a new conversation.'),
      },
    });

    return null;
  }
};

const renameAiChatConversation = (conversationSlug, title) => async (dispatch) => {
  dispatch({ type: 'BEGIN_RENAME_AI_CHAT_CONVERSATION' });

  try {
    const response = await axios({
      method: 'PATCH',
      url: `/api/v1/ai_chat/conversations/${encodeURIComponent(conversationSlug)}`,
      headers: csrfHeader(),
      data: {
        title: title.trim(),
      },
    });

    dispatch({
      type: 'FINISH_RENAME_AI_CHAT_CONVERSATION',
      payload: response,
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: 'FAILED_RENAME_AI_CHAT_CONVERSATION',
      payload: {
        error: parseErrorMessage(error, 'Failed to rename conversation.'),
      },
    });

    return null;
  }
};

const getAiChatMessages = (conversationSlug, { signal } = {}) => async (dispatch) => {
  dispatch({
    type: 'BEGIN_GET_AI_CHAT_MESSAGES',
    payload: { conversationSlug },
  });

  try {
    const response = await axios({
      method: 'GET',
      url: `/api/v1/ai_chat/conversations/${encodeURIComponent(conversationSlug)}/messages`,
      headers: csrfHeader(),
      signal,
    });

    dispatch({
      type: 'FINISH_GET_AI_CHAT_MESSAGES',
      payload: {
        ...response,
        conversationSlug,
      },
    });

    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) return null;

    dispatch({
      type: 'FAILED_GET_AI_CHAT_MESSAGES',
      payload: {
        conversationSlug,
        error: parseErrorMessage(error, 'Failed to load messages.'),
      },
    });

    return null;
  }
};

const buildOptimisticMessages = ({ content, attachment }) => {
  const optimisticSeed = `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  const userMessageId = `optimistic-user-${optimisticSeed}`;
  const assistantMessageId = `optimistic-assistant-${optimisticSeed}`;
  const nowIso = new Date().toISOString();
  const normalizedContent = content?.trim();

  const optimisticUserMessage = {
    id: userMessageId,
    role: 'user',
    status: 'pending',
    content: normalizedContent || '',
    created_at: nowIso,
    attachments: attachment ? [{
      file_token: `optimistic-file-${optimisticSeed}`,
      original_filename: attachment.name,
      content_type: attachment.type,
      byte_size: attachment.size,
      download_url: null,
    }] : [],
  };

  const optimisticAssistantMessage = {
    id: assistantMessageId,
    role: 'assistant',
    status: 'pending',
    content: '',
    created_at: nowIso,
    attachments: [],
  };

  return {
    optimisticUserMessage,
    optimisticAssistantMessage,
    optimisticIds: [userMessageId, assistantMessageId],
  };
};

const buildOptimisticAssistantMessage = () => {
  const optimisticSeed = `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  const assistantMessageId = `optimistic-assistant-${optimisticSeed}`;
  const nowIso = new Date().toISOString();

  return {
    optimisticAssistantMessage: {
      id: assistantMessageId,
      role: 'assistant',
      status: 'pending',
      content: '',
      created_at: nowIso,
      attachments: [],
    },
    optimisticAssistantId: assistantMessageId,
  };
};

const sendAiChatMessage = (conversationSlug, { content, attachment }) => async (dispatch) => {
  const {
    optimisticUserMessage,
    optimisticAssistantMessage,
    optimisticIds,
  } = buildOptimisticMessages({ content, attachment });

  dispatch({
    type: 'BEGIN_SEND_AI_CHAT_MESSAGE',
    payload: {
      conversationSlug,
      optimisticUserMessage,
      optimisticAssistantMessage,
      optimisticIds,
    },
  });

  const formData = new FormData();

  if (content && content.trim()) {
    formData.append('content', content.trim());
  }

  if (attachment) {
    formData.append('attachment', attachment);
  }

  try {
    const response = await axios({
      method: 'POST',
      url: `/api/v1/ai_chat/conversations/${encodeURIComponent(conversationSlug)}/messages`,
      data: formData,
      headers: {
        ...csrfHeader(),
      },
    });

    dispatch({
      type: 'FINISH_SEND_AI_CHAT_MESSAGE',
      payload: {
        ...response,
        conversationSlug,
        optimisticIds,
        stream: response.data?.stream || null,
      },
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: 'FAILED_SEND_AI_CHAT_MESSAGE',
      payload: {
        conversationSlug,
        optimisticIds,
        error: parseErrorMessage(error, 'Failed to send message.'),
      },
    });

    return null;
  }
};

const retryAiChatMessage = (conversationSlug, failedMessageId) => async (dispatch) => {
  const {
    optimisticAssistantMessage,
    optimisticAssistantId,
  } = buildOptimisticAssistantMessage();

  dispatch({
    type: 'BEGIN_RETRY_AI_CHAT_MESSAGE',
    payload: {
      conversationSlug,
      failedMessageId,
      optimisticAssistantMessage,
      optimisticAssistantId,
    },
  });

  try {
    const response = await axios({
      method: 'POST',
      url: `/api/v1/ai_chat/conversations/${encodeURIComponent(conversationSlug)}/messages/${encodeURIComponent(failedMessageId)}/retry`,
      headers: csrfHeader(),
    });

    dispatch({
      type: 'FINISH_RETRY_AI_CHAT_MESSAGE',
      payload: {
        ...response,
        conversationSlug,
        failedMessageId,
        optimisticAssistantId,
        stream: response.data?.stream || null,
      },
    });

    return response.data;
  } catch (error) {
    dispatch({
      type: 'FAILED_RETRY_AI_CHAT_MESSAGE',
      payload: {
        conversationSlug,
        failedMessageId,
        optimisticAssistantId,
        error: parseErrorMessage(error, 'Failed to retry the message.'),
      },
    });

    return null;
  }
};

const createAiChatDocumentConversation = (recordFileToken, title, viewerPath) => async (dispatch) => {
  dispatch({ type: 'BEGIN_CREATE_AI_CHAT_DOCUMENT_CONVERSATION' });
  try {
    const response = await axios({
      method: 'POST',
      url: '/api/v1/ai_chat/conversations',
      headers: csrfHeader(),
      data: { record_file_token: recordFileToken, title, record_viewer_path: viewerPath },
    });
    dispatch({ type: 'FINISH_CREATE_AI_CHAT_DOCUMENT_CONVERSATION', payload: response });
    return {
      conversationSlug: response.data.conversation?.slug,
      isExisting: response.data.existing === true,
    };
  } catch (error) {
    dispatch({
      type: 'FAILED_CREATE_AI_CHAT_DOCUMENT_CONVERSATION',
      payload: { error: parseErrorMessage(error, 'Failed to start document conversation.') },
    });
    return null;
  }
};

const deleteAiChatConversation = (conversationSlug) => async (dispatch) => {
  dispatch({ type: 'BEGIN_DELETE_AI_CHAT_CONVERSATION' });

  try {
    await axios({
      method: 'DELETE',
      url: `/api/v1/ai_chat/conversations/${encodeURIComponent(conversationSlug)}`,
      headers: csrfHeader(),
    });

    dispatch({
      type: 'FINISH_DELETE_AI_CHAT_CONVERSATION',
      payload: {
        slug: conversationSlug,
      },
    });

    return true;
  } catch (error) {
    dispatch({
      type: 'FAILED_DELETE_AI_CHAT_CONVERSATION',
      payload: {
        error: parseErrorMessage(error, 'Failed to delete conversation.'),
      },
    });

    return false;
  }
};

const applyAiChatStreamDelta = (conversationSlug, assistantMessageId, delta) => ({
  type: 'AI_CHAT_STREAM_DELTA',
  payload: {
    conversationSlug,
    assistantMessageId,
    delta,
  },
});

const resetAiChatStreamMessage = (conversationSlug, assistantMessageId) => ({
  type: 'AI_CHAT_STREAM_RESET',
  payload: {
    conversationSlug,
    assistantMessageId,
  },
});

const completeAiChatStreamMessage = (conversationSlug, assistantMessage) => ({
  type: 'AI_CHAT_STREAM_COMPLETED',
  payload: {
    conversationSlug,
    assistantMessage,
  },
});

const failAiChatStreamMessage = (conversationSlug, assistantMessage, error) => ({
  type: 'AI_CHAT_STREAM_FAILED',
  payload: {
    conversationSlug,
    assistantMessage,
    error,
  },
});

const setAiChatWindowGeometry = ({ position, size }) => ({
  type: 'AI_CHAT_SET_WINDOW_GEOMETRY',
  payload: { position, size },
});

const setAiChatSidebarCollapsed = (collapsed) => ({
  type: 'AI_CHAT_SET_SIDEBAR_COLLAPSED',
  payload: { collapsed },
});

const setViewerChatVisible = (visible) => ({
  type: 'AI_CHAT_SET_VIEWER_CHAT_VISIBLE',
  payload: { visible },
});

export {
  setAiChatVisible,
  setAiChatFeatureEnabled,
  setAiChatNearBottom,
  clearAiChatUnseenMessages,
  selectAiChatConversation,
  getAiChatConversations,
  createAiChatConversation,
  createAiChatDocumentConversation,
  renameAiChatConversation,
  getAiChatMessages,
  sendAiChatMessage,
  retryAiChatMessage,
  deleteAiChatConversation,
  applyAiChatStreamDelta,
  resetAiChatStreamMessage,
  completeAiChatStreamMessage,
  failAiChatStreamMessage,
  setAiChatWindowGeometry,
  setAiChatSidebarCollapsed,
  setViewerChatVisible,
};
